import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import PageBreadcrumb from "../../../../shared/ui/common/PageBreadCrumb";
import PageMeta from "../../../../shared/ui/common/PageMeta";
import { ClinicalAlertList, ClinicalAlertModal, ClinicalAlert } from "../../../clinical_alerts";
import { SessionConfigCard, PatientTermCard } from "../../../user_profile";

import { useTherapist } from "../../../../context/useTherapist";
import { useCenter } from "../../../center";
import { TemplateManager } from "../../../session_templates";
import ComponentCard from "../../../../shared/ui/common/ComponentCard";
import { Bell, CalendarDays, Globe, Layout, Receipt, Zap } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../../../../shared/ui/button/Button";
import { useAiInsightsGate } from "../../../ai_insights";
import { useSubscription } from "../../../subscription";
import TabSwitcher, { type TabDefinition } from '../../../../shared/ui/tabs/TabSwitcher';
import { useAdoptedTools } from "../../../marketplace";
import { SchedulerSettingsPanel } from "../../../scheduler";
import { PaymentSettingsPanel } from "../../../payments";
import {
    PRACTICE_SETTINGS_TAB_PARAM,
    isToolSettingsTab,
    parsePracticeSettingsTab,
    visibleToolSettingsTabs,
    type PracticeSettingsTab,
    type ToolSettingsTab,
} from "../../../../shared/navigation/practiceSettings";
import { useMotionSafe } from '../../../../hooks/useMotionSafe';

const PANEL_ID = 'session-config-panel';

const SessionConfig: React.FC = () => {
    const { t } = useTranslation();
    const { therapist } = useTherapist();
    const { isPremium } = useSubscription();
    const { adopted: aiInsightsAdopted, loading: adoptionLoading } = useAiInsightsGate();
    // Clinical alerts are part of the AI Insights tool.
    const alertsAvailable = adoptionLoading || aiInsightsAdopted;
    const { reduced } = useMotionSafe();
    // A training centre names its people itself — students and trainers — and
    // that wins over the therapist's choice, so the picker would change nothing.
    const { center } = useCenter();
    const isTraining = center?.type === "training";

    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [selectedAlert, setSelectedAlert] = useState<ClinicalAlert | null>(null);

    // Scheduler and Payments are configured here too, but only for a therapist
    // who adopted them — their settings requests are refused for anyone else.
    const { data: adoptedTools, isLoading: toolsLoading } = useAdoptedTools();
    const adoptedToolTabs = visibleToolSettingsTabs((adoptedTools ?? []).map((tool) => tool.slug));

    const [searchParams, setSearchParams] = useSearchParams();
    const requestedTab = parsePracticeSettingsTab(searchParams.get(PRACTICE_SETTINGS_TAB_PARAM));

    // While adoption loads, keep the tool tab a link asked for, so arriving
    // from the Scheduler does not flash General first. Its panel still waits.
    const listedToolTabs: ToolSettingsTab[] =
        toolsLoading && requestedTab && isToolSettingsTab(requestedTab)
            ? [requestedTab]
            : adoptedToolTabs;

    const toolTabDefinitions: Record<ToolSettingsTab, TabDefinition<PracticeSettingsTab>> = {
        scheduler: { id: 'scheduler', label: t("settings.tabs.scheduler"), icon: CalendarDays },
        payments: { id: 'payments', label: t("settings.tabs.payments"), icon: Receipt },
    };

    const tabs: TabDefinition<PracticeSettingsTab>[] = [
        { id: 'language', label: t("settings.tabs.general"), icon: Globe },
        { id: 'templates', label: t("settings.tabs.templates", "Templates"), icon: Layout },
        ...(alertsAvailable ? [{ id: 'alerts' as const, label: t("settings.tabs.alerts"), icon: Bell }] : []),
        ...listedToolTabs.map((tab) => toolTabDefinitions[tab]),
    ];

    // The tab lives in the URL so each tool can link straight to its own.
    // A tab not on offer — tool not adopted, or unknown — opens the default,
    // which is also how 'alerts' falls back to general settings.
    const defaultTab: PracticeSettingsTab = isPremium && aiInsightsAdopted ? 'alerts' : 'language';
    const activeTab: PracticeSettingsTab =
        requestedTab && tabs.some((tab) => tab.id === requestedTab) ? requestedTab : defaultTab;

    const handleTabChange = (tab: PracticeSettingsTab) => {
        setSearchParams({ [PRACTICE_SETTINGS_TAB_PARAM]: tab }, { replace: true });
    };

    const isToolPanelOpen = (tab: ToolSettingsTab) =>
        activeTab === tab && adoptedToolTabs.includes(tab);

    const handleCreateAlert = () => {
        setSelectedAlert(null);
        setIsAlertModalOpen(true);
    };

    const handleEditAlert = (alert: ClinicalAlert) => {
        setSelectedAlert(alert);
        setIsAlertModalOpen(true);
    };

    const handleCloseAlertModal = () => {
        setIsAlertModalOpen(false);
        setSelectedAlert(null);
    };

    return (
        <>
            <PageMeta title={t("settings.pageTitle")} />
            <PageBreadcrumb pageTitle={t("settings.pageTitle")} />

            <div className="w-full mb-8 relative z-30">
                <TabSwitcher
                    tabs={tabs}
                    activeTab={activeTab}
                    onChange={handleTabChange}
                    label={t("settings.pageTitle")}
                    layoutId="activeTabConfig"
                    panelId={PANEL_ID}
                />
            </div>

            <AnimatePresence mode="wait">
                <motion.div
                    key={activeTab}
                    id={PANEL_ID}
                    role="tabpanel"
                    initial={{ opacity: 0, y: reduced ? 0 : 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: reduced ? 0 : -6 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-8"
                >
                    {activeTab === 'alerts' && (
                        <div className="max-w-5xl mx-auto w-full">
                            {isPremium ? (
                                <ComponentCard
                                    glass
                                    title={
                                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full gap-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-xl bg-brand-500/10 text-brand-500 flex items-center justify-center shrink-0">
                                                    <Bell className="w-5 h-5" />
                                                </div>
                                                <div className="min-w-0">
                                                    <span className="block text-lg font-bold text-content-primary truncate">
                                                        {t("settings.tabs.alerts")}
                                                    </span>
                                                    <p className="hidden sm:block text-xs text-content-tertiary font-normal">
                                                        {t("settings.alertsDescription")}
                                                    </p>
                                                </div>
                                            </div>
                                            <Button
                                                onClick={handleCreateAlert}
                                                className="shadow-lg shadow-brand-500/20 w-full sm:w-auto"
                                            >
                                                {t("common.create")}
                                            </Button>
                                        </div>
                                    }
                                >
                                    <ClinicalAlertList onEdit={handleEditAlert} />
                                </ComponentCard>
                            ) : (
                                <div className="zen-glass p-8 sm:p-12 rounded-3xl sm:rounded-3xl relative overflow-hidden group shadow-raised border border-brand-500/20 text-center flex flex-col items-center">
                                    <div className="w-16 h-16 rounded-2xl bg-brand-500/10 text-brand-500 flex items-center justify-center mb-8">
                                        <Zap className="w-8 h-8" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-content-primary mb-4">
                                        {t("settings.tabs.alerts")}
                                    </h3>
                                    <p className="text-content-tertiary mb-10 leading-relaxed max-w-md">
                                        {t("subscription.plus.betaOnly")} {t("sidebar.plusPlanDesc")}
                                    </p>
                                    <Link
                                        to="/subscription"
                                        className="bg-brand-600 hover:bg-brand-700 text-white px-10 py-4 rounded-2xl font-bold transition-all shadow-lg shadow-brand-500/20 active:scale-95"
                                    >
                                        {t("subscription.plus.button")}
                                    </Link>
                                    <div className="absolute -right-10 -bottom-10 w-48 h-48 bg-brand-500/5 rounded-full blur-3xl" />
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'language' && (
                        <div className="max-w-4xl mx-auto w-full space-y-6">
                            <SessionConfigCard therapist={therapist} />
                            {!isTraining && <PatientTermCard therapist={therapist} />}
                        </div>
                    )}

                    {activeTab === 'templates' && (
                        <div className="max-w-6xl mx-auto w-full">
                            <TemplateManager />
                        </div>
                    )}

                    {isToolPanelOpen('scheduler') && (
                        <div className="max-w-4xl mx-auto w-full">
                            <SchedulerSettingsPanel />
                        </div>
                    )}

                    {isToolPanelOpen('payments') && (
                        <div className="max-w-3xl mx-auto w-full">
                            <PaymentSettingsPanel />
                        </div>
                    )}


                </motion.div>
            </AnimatePresence>

            <ClinicalAlertModal
                isOpen={isAlertModalOpen}
                onClose={handleCloseAlertModal}
                alertToEdit={selectedAlert}
            />
        </>
    );
};

export default SessionConfig;
